<template>
  <div class="container-fluid">
    <page-header
      :page="page"      
      :left-side-class="'col-xl-8'"
      :right-side-class="
        'col-xl-4 align-items-end d-flex pb-2 justify-content-end'
      "
    >
      <template v-slot:customActions>
        <action-button
          v-if="!existsCurrentYearWeeksConfiguration"
          variant="success m-1 mr-2"
          i="fas fa-file-plus pr-1"
          :responsive="true"
          @click="generateCurrentYearConfiguration"
        >
          Generar Año Académico Actual
        </action-button>
        <action-button
          v-if="!existsFutureYearWeeksConfiguration"
          variant="success m-1"
          i="fas fa-file-plus pr-1"
          :responsive="true"
          @click="generateFutureYearConfiguration"
        >
          Generar Próximo Año Académico
        </action-button>
      </template>
    </page-header>
    
    <filter-manager
      v-model="filters"
      :display-advanced="true"
      :filters="filtersHeader"
      @academicYear="onAcademicYearChange"
      @dateFrom="onDateFromChange"   
      @search="handleSearch"
    />

    <CustomSeparator
      text="Resultados"
      class="mt-4 custom-separator-line"
    />    

    <div v-if="results && results.length" class="row pt-2">
      <div class="col-12">
        <ResultRow
          v-for="(item, index) in results"
          :key="`row-item-${index}`"
          :item="item"
          :index="index"
          @update-week="updateWeekHandler(item, $event)"
        />
      </div>
    </div>
    
    <CustomResultNotFound
      v-else
      class="mt-4"
      text="No hay Información Disponible"
    />

    <div class="row pt-2">
      <div class="col-12 pt-0">
        <pagination
          v-if="pagination.totalItemCount > pagination.count"
          :data="pagination"
          @pagination-go-page="handlePagedChanged"
        />
      </div>
    </div>
  </div>
</template>
  
<script>
import PageHeader from 'PageHeader';
import CustomSeparator from 'custom-separator';
import ActionButton from 'ActionButton';
import FilterManager from 'FilterManager';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from "pagination";
import ResultRow from "./components/ResultRow";
import {
  getInitFilters as _getInitFilters,
  validateCurrentYearWeeksConfiguration as _validateCurrentYearWeeksConfiguration,
  validateFutureYearWeeksConfiguration as _validateFutureYearWeeksConfiguration,
  generateCurrentYearWeeksConfiguration as _generateCurrentYearWeeksConfiguration,
  generateFutureYearWeeksConfiguration as _generateFutureYearWeeksConfiguration,
  search as _search,
} from '@/services/confRelatedServiceAcademicWeek.js';
import academicYearService from '@/services/academicyear.js';
import { cloneDeep } from 'lodash';
import mixAlert from "@/mixin/alert.mixin";

const DEFAULT_PAGE_SIZE = 10;

export default {
  name: 'Main',
  mixins: [mixAlert],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    ActionButton,
    CustomResultNotFound,
    Pagination,
    ResultRow
  },
  data() {
    return {
      page: {
        icon: 'fas fa-calculator',
        title: 'Configuración Semanas Lectivas de Servicios Relacionados',
        routes: ['Inicio', 'Operaciones', 'Configuración Semanas Lectivas de Servicios Relacionados'],
      },
      collections: {
        academicYears: [],
        weekStatuses: [],
        billingWeekStatuses: [],
        months: [],
      },
      filters: {
        academicYear: null,
        weekStatus: null,
        month: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
        data: [],
      },
      results: [],
      isExistingCurrentAcademicYear: false,
      isExistingFutureAcademicYear: false,
    };
  },
  computed: {
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-md-4 ml-0 mr-0',
          classContainer: 'w-100',
          validations: 'required',
          clearable: false,
          error: 'Debe seleccionar el Año Académico.'
        },  
        {
          name: 'weekStatus',
          component: 'CustomDropDown',
          options: this.collections.weekStatuses,
          value: this.filters.weekStatus,
          placeholder: 'Estatus Semana SR',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-4 ml-0 mr-0',
          classContainer: 'w-100',          
        },
        {
          name: 'month',
          component: 'CustomDropDown',
          options: this.collections.months,
          value: this.filters.month,
          placeholder: 'Mes',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-md-4 ml-0 mr-0',
          classContainer: 'w-100',          
        },
        {
          name: 'dateFrom',
          component: 'custom-date-picker',
          label: 'Fecha Desde:',
          placeholder: 'DD/MM/YYYY',
          classContainer: 'date-width-container',
          class: 'col-md-3 ml-0 mr-0',
          value: this.filters.dateFrom,
          isAdvanced: true,
        },
        {
          name: 'dateTo',
          component: 'custom-date-picker',
          label: 'Fecha Hasta:',
          placeholder: 'DD/MM/YYYY',
          classContainer: 'date-width-container',
          class: 'col-md-3 ml-0 mr-0',
          value: this.filters.dateTo,
          isAdvanced: true,
          ...(this.filters.dateFrom && { minDate: this.filters.dateFrom }),
        },
      ];   
    },
    existsCurrentYearWeeksConfiguration() {
      return this.isExistingCurrentAcademicYear;
    },
    existsFutureYearWeeksConfiguration() {
      return this.isExistingFutureAcademicYear;
    },
  },
  async mounted() {
    await this.validateCurrentYearConfiguration();
    await this.validateFutureYearConfiguration();
    
    await this.loadInitFilters();
  },
  methods: {
    async loadInitFilters() {
      const { data: collections } = await _getInitFilters();
      this.collections = { ...collections };
    },
    async validateCurrentYearConfiguration() {
      const { data: existsCurrentYearWeeks } = await _validateCurrentYearWeeksConfiguration();
      this.isExistingCurrentAcademicYear = existsCurrentYearWeeks;
    },
    async validateFutureYearConfiguration() {
      const { data: existsFutureYearWeeks } = await _validateFutureYearWeeksConfiguration();
      this.isExistingFutureAcademicYear = existsFutureYearWeeks;
    },
    async generateCurrentYearConfiguration() {
      await _generateCurrentYearWeeksConfiguration();
      await this.validateCurrentYearConfiguration();
    },
    async generateFutureYearConfiguration() {
      await _generateFutureYearWeeksConfiguration();
      await this.validateFutureYearConfiguration();
    },
    async onAcademicYearChange(value) {
      if (!value?.id) return;

      const { data: monthsList } = await academicYearService.getAvailableMonths(value.id);
      this.collections.months = [...monthsList];
    },
    onDateFromChange(value) {
      if (value && value instanceof Date && !isNaN(value)
        && this.filters.dateTo <= this.filters.dateFrom) {
        this.filters.dateTo = null;
      };
    },
    async handleSearch() {
      if(!this.filters.academicYear?.id) return;

      const pageIndex = 1;
      const payload = this.fillPayload(pageIndex);
      await this.search(payload);
    },
    async handlePagedChanged(currentPage) {
      const payload = this.fillPayload(currentPage);
      await this.search(payload);
    },
    fillPayload(page) {
      const payload = {
        academicYearId: this.filters.academicYear?.id,
        isRelatedServiceFlag: this.filters.weekStatus?.id === undefined
          ? null : this.filters.weekStatus.id === 1,
        monthNumber: this.filters.month?.id,
        dateFrom: this.filters.dateFrom,
        dateTo: this.filters.dateTo,
        pageIndex: page ? page : 1,
        pageSize: DEFAULT_PAGE_SIZE,
      };
      return payload;
    },
    async search(payload) {      
      try {
        const { data: result } = await _search(payload);
       
        this.results = result.data;
        this.pagination = { ...result };
      } catch(err) {
        this.ShowToast(
          '',
          err.response?.data?.message || err.message,
          'error'
        );
      };
    },
    async updateWeekHandler(item, updatedWeek) {
      const _updatedWeek = await cloneDeep(updatedWeek);
      item.isRelatedServiceFlag = _updatedWeek.isRelatedServiceFlag;
      item.daysOfWeek = _updatedWeek.daysOfWeek;
    },
  },
  
};
</script>
<style scoped>
</style>
  